import React, { useCallback, useEffect, useState } from 'react';
import Button, { ButtonGroup, LoadingButton } from '@atlaskit/button';
import Form, { FormFooter, FormHeader, FormSection, RequiredAsterisk } from '@atlaskit/form';
import {
  NotificationTypes,
  errorNotificationBuilder,
  successNotificationBuilder,
  useNotification,
  useSidebar,
  useFeatureToggle,
} from '@css-xp/base';
import { bulkAssignRoleResourceToPrincipal } from '@atlassian/temp-proxies';
import { AddContactFields } from '@css-xp/ppc-add-contact-fields';
import { failureMessage } from '@css-xp/ppc-common';
import { ENABLE_DESIGN_ITERATION_1 } from '@css-xp/ppc-constants';

import './add-billing-admin.scss';

export const validateEmailDomain = (email: string, validDomains: string[]) => {
  const domainFromEmail = email.split('@')[1];
  const matchedPartnerDomain = domainFromEmail && validDomains.includes(domainFromEmail.toLowerCase());
  if (!matchedPartnerDomain) {
    return `Email address must have the same domain as the partner account or its associated accounts`;
  }
};

interface AddBillingAdminProps {
  txaId: string;
  validDomains: string[];
  currentUserList: string[];
  refetch: () => void;
}

export const AddBillingAdmin = ({ txaId, validDomains, currentUserList, refetch }: AddBillingAdminProps) => {
  const { closeSidebar } = useSidebar();
  const { notificationSent, sendSuccessNotification, setNotificationSent, sendErrorNotification } = useNotification();

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isSaveDisabled, setIsSaveDisabled] = useState<boolean>(true);
  const [atlassianAccountIds, setAtlassianAccountIds] = useState<Array<string>>(['']);
  const [atlassianAccountEmails, setAtlassianAccountEmails] = useState<Array<string>>(['']);

  const [isEnabled] = useFeatureToggle();
  const isDesignIteration1Enabled = isEnabled(ENABLE_DESIGN_ITERATION_1);

  const handleFormSubmit = useCallback(async () => {
    try {
      setIsLoading(true);
      const requestPayload = {
        assignments: atlassianAccountIds.map((id) => {
          return {
            role: 'transaction-account/billing-admin',
            resourceAri: `ari:cloud:commerce::transaction-account/${txaId}`,
            userId: id,
          };
        }),
      };
      await bulkAssignRoleResourceToPrincipal(requestPayload, txaId);
      const commonMessage = `You've added ${atlassianAccountEmails.length} billing admins`;
      const message = (
        <div>
          {commonMessage}:
          <ul className="mt-4">
            {atlassianAccountEmails?.map((email) => (
              <li key={email}>{email}</li>
            ))}
          </ul>
        </div>
      );
      if (!notificationSent) {
        const notificationOptions = successNotificationBuilder()
          .isVersion(NotificationTypes.FLAG)
          .withHeader('Update successful')
          .withMessage(message)
          .withDismissAfter(5000)
          .build();
        sendSuccessNotification(notificationOptions);
        setNotificationSent(true);
      }
      setIsLoading(false);
      closeSidebar();
      refetch();
    } catch (err) {
      if (!notificationSent) {
        const notificationOptions = errorNotificationBuilder()
          .isVersion(NotificationTypes.FLAG)
          .withHeader('Update unsuccessful')
          .withMessage(failureMessage())
          .withDismissAfter(5000)
          .build();
        sendErrorNotification(notificationOptions);
        setNotificationSent(true);
      }
      setIsLoading(false);
    }
  }, [
    atlassianAccountIds,
    atlassianAccountEmails,
    closeSidebar,
    notificationSent,
    refetch,
    sendErrorNotification,
    sendSuccessNotification,
    setNotificationSent,
    txaId,
  ]);

  useEffect(() => {
    if (atlassianAccountIds.findIndex((id) => !id) >= 0) {
      setIsSaveDisabled(true);
    } else {
      setIsSaveDisabled(false);
    }
  }, [atlassianAccountIds]);

  const renderFooterButtons = () => {
    return (
      <>
        <Button appearance={isDesignIteration1Enabled ? undefined : 'subtle'} onClick={closeSidebar}>
          Cancel
        </Button>
        <LoadingButton
          appearance="primary"
          isLoading={isLoading}
          isDisabled={isSaveDisabled}
          type="submit"
          testId="save-billing-admin-button"
        >
          {isDesignIteration1Enabled ? 'Add' : 'Save'}
        </LoadingButton>
      </>
    );
  };

  return (
    <div
      className={isDesignIteration1Enabled ? undefined : 'add-billing-admin-sidebar-ppc'}
      data-testid="add-billing-admin-sidebar"
    >
      <Form onSubmit={handleFormSubmit}>
        {({ formProps }) => (
          <form {...formProps}>
            <FormHeader title={!isDesignIteration1Enabled ? 'Add billing admin' : undefined}>
              {isDesignIteration1Enabled && (
                <p aria-hidden="true">
                  Required fields are marked with an asterisk <RequiredAsterisk />
                </p>
              )}
            </FormHeader>
            <FormSection>
              {!isDesignIteration1Enabled && (
                <h4 className="billing-admin-text" data-testid="billing-admin-email-label">
                  Email address <span className="required-indicator">*</span>
                </h4>
              )}
              <AddContactFields
                setAtlassianAccountIds={setAtlassianAccountIds}
                atlassianAccountIds={atlassianAccountIds}
                setAtlassianAccountEmails={setAtlassianAccountEmails}
                atlassianAccountEmails={atlassianAccountEmails}
                currentUserList={currentUserList}
                userAlreadyExistText="Billing admin already exists"
                emailFieldText={isDesignIteration1Enabled ? '' : 'Enter email address'}
                nameFieldText="Enter full name"
                addButtonText="Add another email address"
                validateEmailField={(email) => validateEmailDomain(email, validDomains)}
                isAddNewContactDisabled={isDesignIteration1Enabled}
                emailFieldLabel={isDesignIteration1Enabled ? 'Email address' : ''}
                {...(isDesignIteration1Enabled && { version: 2 })}
                validMessageText={isDesignIteration1Enabled ? 'Valid billing admin' : ''}
              />
            </FormSection>
            <div className={isDesignIteration1Enabled ? '' : 'form-footer'}>
              <FormFooter align="end">
                {isDesignIteration1Enabled ? renderFooterButtons() : <ButtonGroup>{renderFooterButtons()}</ButtonGroup>}
              </FormFooter>
            </div>
          </form>
        )}
      </Form>
    </div>
  );
};
